/**
 * GlobalCreditListener — mounts inside AppShell and shows a toast whenever
 * the tenant's credit balance crosses a $5 / $1 / $0 threshold.
 *
 * Pairs with the backend broadcast in services/credit_service.deduct_credits()
 * which emits `CREDIT_THRESHOLD_CROSSED` on Channel.GLOBAL.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useWebSocket, EventTypes, Channels } from '../hooks/useWebSocket';

export default function GlobalCreditListener() {
  const navigate = useNavigate();

  const handleCreditThreshold = useCallback((data) => {
    if (!data) return;
    const severity = data.severity || 'info';
    const balanceAfter = typeof data.balance_after === 'number' ? data.balance_after : null;
    const goToBilling = () => navigate('/billing?tab=usage-costs');

    if (severity === 'critical') {
      toast.error(
        `Credits depleted — AI actions paused. Top up to continue.`,
        {
          duration: 10000,
          action: { label: 'Top up', onClick: goToBilling },
        }
      );
    } else if (severity === 'warning') {
      toast.warning(
        `Credits low — $${balanceAfter?.toFixed(2) ?? '?'} remaining`,
        {
          duration: 8000,
          action: { label: 'Top up', onClick: goToBilling },
        }
      );
    } else {
      toast.info(
        `Credits running low — $${balanceAfter?.toFixed(2) ?? '?'} remaining`,
        {
          duration: 6000,
          action: { label: 'Top up', onClick: goToBilling },
        }
      );
    }
  }, [navigate]);

  useWebSocket(
    [Channels.GLOBAL],
    { [EventTypes.CREDIT_THRESHOLD_CROSSED]: handleCreditThreshold },
    { showToasts: false, autoConnect: true }
  );

  return null;
}
